import { CustomerIoAdapter } from "./adapters/customerio.adapter";
import { GTMAdapter } from "./adapters/gtm.adapter";
import { MixpanelAdapter } from "./adapters/mixpanel.adapter";
import { PosthogAdapter } from "./adapters/posthog.adapter";
import { SegmentAdapter } from "./adapters/segment.adapter";
import type { ProviderKey } from "./config";
import type { AnalyticsAdapter } from "./types";

/**
 * Type for adapter constructors
 */
type AdapterConstructor = new () => AnalyticsAdapter;

/**
 * Registry map that links provider keys to their adapter implementations
 */
const providerRegistry = new Map<ProviderKey, AdapterConstructor>();

// Register built-in adapters
providerRegistry.set("segment", SegmentAdapter);
providerRegistry.set("mixpanel", MixpanelAdapter);
providerRegistry.set("gtm", GTMAdapter);
providerRegistry.set("posthog", PosthogAdapter);
providerRegistry.set("customerio", CustomerIoAdapter);

/**
 * Registers a new analytics adapter for a given provider key
 * This allows extending the system with new providers without modifying core code
 *
 * @param key - The provider key (must be a valid ProviderKey)
 * @param adapterClass - The adapter class constructor
 */
export function registerAnalyticsAdapter(key: ProviderKey, adapterClass: AdapterConstructor): void {
  providerRegistry.set(key, adapterClass);
}

/**
 * Creates an adapter instance for the given provider key
 *
 * @param key - The provider key
 * @returns An adapter instance, or null if the key is not registered
 */
export function createAdapterForProvider(key: ProviderKey): AnalyticsAdapter | null {
  const AdapterClass = providerRegistry.get(key);
  if (!AdapterClass) {
    return null;
  }
  return new AdapterClass();
}

/**
 * Creates adapter instances for multiple provider keys
 *
 * @param keys - Array of provider keys
 * @returns Array of successfully created adapter instances
 */
export function createAdaptersForProviders(keys: Array<ProviderKey>): Array<AnalyticsAdapter> {
  const adapters: Array<AnalyticsAdapter> = [];

  for (const key of keys) {
    const adapter = createAdapterForProvider(key);
    if (adapter) {
      adapters.push(adapter);
    }
  }

  return adapters;
}
